/**
 * LoginPage — IdeaWorks Multi-User Auth
 * Admin users: login with Convex-managed keys (no OpenRouter key needed)
 * Regular users: register with their own OpenRouter API key
 */
import { useState } from 'react';
import axios from 'axios';
import { Brain, Eye, EyeOff, RefreshCw, ExternalLink, Check, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage({ onLogin }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [openrouterKey, setOpenrouterKey] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      if (mode === 'login') {
        const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
        localStorage.setItem('ideaworks_token', data.token);
        localStorage.setItem('ideaworks_user', JSON.stringify(data.user));
        onLogin?.(data);
      } else {
        const { data } = await axios.post(`${API}/api/auth/register`, {
          email, password, name, openrouter_key: openrouterKey,
        });
        localStorage.setItem('ideaworks_token', data.token);
        localStorage.setItem('ideaworks_user', JSON.stringify(data.user));
        onLogin?.(data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Authentication failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: '#060610' }}>
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse, rgba(124,94,250,0.08) 0%, transparent 70%)', filter: 'blur(40px)' }} />
      </div>

      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(124,94,250,0.3), rgba(166,142,255,0.1))', border: '1px solid rgba(124,94,250,0.4)' }}>
            <Brain size={28} style={{ color: '#a68eff' }} />
          </div>
          <div className="text-2xl font-black text-white">IdeaWorks</div>
          <div className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>AI Content Platform</div>
        </div>

        <div className="rounded-2xl overflow-hidden"
          style={{ background: '#0a0a16', border: '1px solid rgba(124,94,250,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>

          {/* Tab toggle */}
          <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {['login', 'register'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); }}
                className="flex-1 py-3 text-sm font-bold transition-all capitalize"
                style={{
                  color: mode === m ? '#a68eff' : 'rgba(255,255,255,0.35)',
                  borderBottom: mode === m ? '2px solid #7c5efa' : '2px solid transparent',
                }}>
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="p-6 space-y-3">
            {mode === 'register' && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  data-testid="auth-name-input"
                  placeholder="Your name"
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
              </div>
            )}

            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Email *</label>
              <input value={email} onChange={e => setEmail(e.target.value)}
                data-testid="auth-email-input"
                type="email" required
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Password *</label>
              <div className="relative">
                <input value={password} onChange={e => setPassword(e.target.value)}
                  data-testid="auth-password-input"
                  type={showPass ? 'text' : 'password'} required minLength={6}
                  placeholder="Min 6 characters"
                  className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
                <button type="button" onClick={() => setShowPass(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(255,255,255,0.3)' }}>
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  OpenRouter API Key
                  <span className="ml-2 normal-case font-normal" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    (optional for first/admin account)
                  </span>
                  <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer"
                    className="ml-2 inline-flex items-center gap-0.5 normal-case font-normal hover:underline"
                    style={{ color: '#a68eff' }}>
                    Get free key <ExternalLink size={9} />
                  </a>
                </label>
                <div className="relative">
                  <input value={openrouterKey} onChange={e => setOpenrouterKey(e.target.value)}
                    data-testid="auth-openrouter-key"
                    type="text"
                    placeholder="sk-or-v1-... (leave blank if you are the admin)"
                    autoComplete="new-password"
                    name="openrouter_api_key_field"
                    className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
                  <button type="button" onClick={() => setShowKey(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.3)' }}>
                    {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <div className="mt-1.5 p-2 rounded-xl flex items-start gap-2"
                  style={{ background: 'rgba(61,255,160,0.05)', border: '1px solid rgba(61,255,160,0.15)' }}>
                  <Shield size={10} style={{ color: '#3dffa0', flexShrink: 0, marginTop: 1 }} />
                  <div className="text-[9px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    The first account registered becomes admin and uses platform keys. Additional users need their own OpenRouter key.
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 rounded-xl text-xs" style={{ background: 'rgba(254,44,85,0.1)', border: '1px solid rgba(254,44,85,0.25)', color: '#fe2c55' }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading || !email || !password}
              data-testid="auth-submit-btn"
              className="w-full py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-40 mt-1"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
              {loading ? <RefreshCw size={14} className="animate-spin inline mr-2" /> : null}
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
        </div>

        <div className="text-center mt-4 text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
          Platform keys are managed by the admin. Regular users use their own OpenRouter key.
        </div>
      </div>
    </div>
  );
}
